/** Public Chrome Web Store URL, injected by `define` in vite.config.ts. */
const EXTENSION_URL = process.env.VITE_EXTENSION_URL || '';

const WEB_STORE_LINK = {
  label: 'Open the Chrome Web Store',
  href: EXTENSION_URL,
};

export type SupportStep = {
  text: string;
  link?: { label: string; href: string };
};

export type SupportGuide = {
  /** Anchor id — these are linked from outside the app, so keep them stable. */
  id: string;
  title: string;
  navLabel: string;
  summary: string;
  icon: string;
  steps: SupportStep[];
};

export type SupportGuideGroup = {
  label: string;
  guides: SupportGuide[];
};

export type SupportCost = {
  name: string;
  description: string;
  when: string;
};

export const SUPPORT_PHONE = '+1 (703) 405-8794';
export const SUPPORT_PHONE_HREF = 'tel:+17034058794';
export const SUPPORT_EMAIL = 'support@unbeatableloans.com';
export const SUPPORT_EMAIL_HREF = 'mailto:support@unbeatableloans.com';

export const COSTS_SECTION_ID = 'costs';

export const supportGuideGroups: SupportGuideGroup[] = [
  {
    label: 'Getting started',
    guides: [
      {
        id: 'account',
        title: 'Secure your account',
        navLabel: 'Secure an account',
        summary:
          'Turn on email verification so every sign-in has to be confirmed from your inbox.',
        icon: 'fa-shield-check',
        steps: [
          { text: 'Sign in with your email, then open your account page.' },
          {
            text: 'Add your name. You can also choose a custom username here.',
          },
          {
            text: 'Select "Enable Security" to require email verification at sign-in.',
          },
          {
            text: 'Open the verification link we send to your email address.',
          },
          {
            text: 'While account security is on, you confirm every sign-in from your inbox.',
          },
        ],
      },
      {
        id: 'use-extension',
        title: 'Use the Chrome extension',
        navLabel: 'Use the Chrome extension',
        summary:
          'The extension reads property details from any website and passes them to Unbeatable Loans.',
        icon: 'fa-puzzle-piece',
        steps: [
          {
            text: 'Make sure you are on a recent version of Google Chrome.',
          },
          {
            text: 'Install the Unbeatable Loans extension.',
            link: WEB_STORE_LINK,
          },
          {
            text: 'Pin it to your toolbar: select the puzzle piece icon, then the pin next to Unbeatable Loans.',
          },
          {
            text: 'Click the Unbeatable Loans icon in your toolbar to activate the extension.',
          },
          {
            text: 'With the extension active, pick the property information you want and it travels to the platform.',
          },
          {
            text: 'Click the icon again to deactivate the extension.',
          },
        ],
      },
    ],
  },
  {
    label: 'Find a home',
    guides: [
      {
        id: 'add-property',
        title: 'Add a property',
        navLabel: 'Add a property',
        summary:
          'Save a listing from any site to your account using the Chrome extension.',
        icon: 'fa-house-circle-check',
        steps: [
          {
            text: 'Install the extension, then sign in to your Unbeatable Loans account.',
            link: WEB_STORE_LINK,
          },
          {
            text: 'Open the listing you want on any listing service or property detail website.',
          },
          {
            text: 'Click the Unbeatable Loans icon in your toolbar to activate the extension.',
          },
          {
            text: 'Confirm the property information when the extension prompts you.',
          },
          {
            text: 'Fill in anything missing, or re-pick a detail with your cursor to correct it.',
          },
          {
            text: 'Click "Add to Unbeatable" to save the property to your account.',
          },
          {
            text: 'To jump straight to the platform, click the emblem on the extension popup before it disappears.',
          },
        ],
      },
    ],
  },
  {
    label: 'Financing',
    guides: [
      {
        id: 'create-budget',
        title: 'Create a home shopping budget',
        navLabel: 'Create a budget',
        summary:
          'See what you can afford at current rates before you start touring homes.',
        icon: 'fa-calculator-simple',
        steps: [
          {
            text: 'Sign in, then go to Profile → Qualifications.',
          },
          {
            text: 'Enter your monthly income, credit score, down payment, and existing monthly obligations.',
          },
          {
            text: 'Review what the budgeting tool says you can afford, and adjust the inputs as needed.',
          },
          {
            text: 'Save — this powers your purchasing-power estimate everywhere in the app.',
          },
          {
            text: 'Estimates are based on current interest rates, so check back as rates move.',
          },
        ],
      },
      {
        id: 'get-approved',
        title: 'Get approved for a mortgage',
        navLabel: 'Get approved',
        summary:
          'Approval is what unlocks offers that listing agents can accept on your behalf.',
        icon: 'fa-badge-check',
        steps: [
          {
            text: 'Complete your financial profile first (see "Create a home shopping budget").',
          },
          {
            text: 'Open a saved property and use the "Next Step" button in its Deal Room to start your loan application.',
          },
          {
            text: "From there you're taken into your loan officer's application in our secure loan origination system.",
          },
          {
            text: 'Upload your supporting documents: proof of income, bank statements, and identification.',
          },
          {
            text: 'Submit the application and stand by for a call from one of our loan officers.',
          },
          {
            text: 'Our team reviews the application and sends you an official approval decision.',
          },
          {
            text: 'Once approved, you can submit offers that listing agents can accept on your behalf to enter into contract.',
          },
        ],
      },
    ],
  },
  {
    label: 'Making offers',
    guides: [
      {
        id: 'submit-offer',
        title: 'Submit an offer',
        navLabel: 'Submit an offer',
        summary: 'Write and send an offer on any property you are approved on.',
        icon: 'fa-file-signature',
        steps: [
          {
            text: 'Sign in and open "Properties" in the top navigation.',
          },
          { text: 'Select the property you want to make an offer on.' },
          { text: 'Click "Start Offer".' },
          {
            text: 'Fill in the offer details, including the amount and any contingencies.',
          },
          { text: 'Submit the offer for review.' },
          {
            text: 'We email you a confirmation once the offer has been sent to the seller.',
          },
        ],
      },
    ],
  },
];

export const supportGuides: SupportGuide[] = supportGuideGroups.flatMap(
  (group) => group.guides
);

export const supportCosts: SupportCost[] = [
  {
    name: 'Credit report fee',
    description:
      'Covers pulling your credit report as part of the mortgage approval process.',
    when: 'During approval',
  },
  {
    name: 'Application fee',
    description: 'Non-refundable fee for processing your mortgage application.',
    when: 'At closing',
  },
  {
    name: 'Origination fee',
    description: 'Charged by the lender for processing the loan.',
    when: 'At closing',
  },
  {
    name: 'Appraisal fee',
    description: 'Pays for the professional appraisal of the property.',
    when: 'On contract acceptance',
  },
  {
    name: 'Closing costs',
    description:
      'Third-party fees to close the loan, including attorney fees, title insurance, and recording fees.',
    when: 'At closing',
  },
];

/** Flattened text used by the on-page topic search. */
export const guideSearchText = (guide: SupportGuide): string =>
  [
    guide.title,
    guide.navLabel,
    guide.summary,
    ...guide.steps.map((step) => step.text),
  ]
    .join(' ')
    .toLowerCase();

export const costsSearchText = (): string =>
  [
    'Costs involved',
    ...supportCosts.flatMap((cost) => [cost.name, cost.description, cost.when]),
  ]
    .join(' ')
    .toLowerCase();
